import { useTranslation } from 'react-i18next';
import { IconTag } from '@tabler/icons-react';
import { formatPrice } from '@/lib/format-utils';
import { Reservation } from '@turizi/shared';

interface Props {
    reservation: Reservation;
    className?: string;
}

export default function ReservationOriginalPriceTotals({
    reservation,
    className = '',
}: Props) {
    const { t } = useTranslation();

    const bookings = reservation?.service_bookings || [];
    const currentTotalPerCurrency: Record<string, number> = reservation?.price_per_currency || {};

    // Fallback current subtotal per currency directly from service bookings if price_per_currency is empty
    const fallbackCurrentPerCurrency: Record<string, number> = {};
    bookings
        .filter(b => b.money_in_currency)
        .forEach((b) => {
            const curr = b.money_in_currency;
            const amt = Number(b.money_in_amount || 0);
            fallbackCurrentPerCurrency[curr] = (fallbackCurrentPerCurrency[curr] || 0) + amt;
        });

    const activeCurrentTotals = Object.keys(currentTotalPerCurrency).length > 0
        ? currentTotalPerCurrency
        : fallbackCurrentPerCurrency;

    // Compute original base subtotals per currency from service bookings
    const originalSubtotalPerCurrency: Record<string, number> = {};
    bookings
        .filter(b => b.money_in_currency)
        .forEach((b) => {
            const amt = b.price_info?.money_in_amount !== undefined && b.price_info?.money_in_amount !== null
                ? Number(b.price_info.money_in_amount)
                : (b.money_in_amount_exchanged !== undefined && b.money_in_amount_exchanged !== null
                    ? Number(b.money_in_amount_exchanged)
                    : Number(b.money_in_amount || 0));

            const curr = b.price_info?.money_in_currency || b.money_in_currency_exchanged || b.money_in_currency;
            originalSubtotalPerCurrency[curr] = (originalSubtotalPerCurrency[curr] || 0) + amt;
        });

    const origCurrencies = Object.keys(originalSubtotalPerCurrency);
    const currCurrencies = Object.keys(activeCurrentTotals);

    if (origCurrencies.length === 0 || bookings.length === 0) {
        return null;
    }

    // Check if there is any difference between original total and current total
    const hasDifferentCurrencies = origCurrencies.length !== currCurrencies.length ||
        origCurrencies.some(c => !currCurrencies.includes(c));

    const hasDifferentAmounts = origCurrencies.some((curr) => {
        const origAmt = Math.round((originalSubtotalPerCurrency[curr] || 0) * 100) / 100;
        const currentAmt = Math.round((activeCurrentTotals[curr] || 0) * 100) / 100;
        return origAmt !== currentAmt;
    });

    if (!hasDifferentCurrencies && !hasDifferentAmounts) {
        return null;
    }

    return (
        <div className={`${className}`}>
            <div className="bg-slate-50 border border-slate-200/80 rounded-lg p-2.5 shadow-2xs space-y-1.5">
                <div className="flex items-center gap-1.5 border-b border-slate-200/60 pb-1 text-[10px] font-bold text-slate-500 uppercase tracking-wider">
                    <span>{t('original_total_price')}</span>
                </div>
                <div className="space-y-1.5">
                    {origCurrencies.map((currency) => {
                        const total = originalSubtotalPerCurrency[currency] || 0;
                        return (
                            <div key={currency}>
                                <div className="flex items-baseline justify-end gap-1 font-bold text-slate-800 whitespace-nowrap shrink-0 ml-auto">
                                    <span className="font-light text-slate-400">$</span>
                                    <span className="text-sm tracking-tight font-extrabold">{formatPrice(total)}</span>
                                    <span className="text-[11px] font-semibold text-slate-500 uppercase ml-0.5">{currency}</span>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}
